/**
 * Codificador EXIF mínimo para JPEG (spec 2026-09-23, fotos geolocalizadas, D7).
 *
 * ── Por qué propio y no una librería ───────────────────────────────────────
 * Ningún navegador escribe EXIF (un `canvas` los tira todos) y las librerías
 * que lo hacen están abandonadas o rotas: `piexifjs` no se toca desde 2019,
 * escribe los IFD de Exif y GPS sin el puntero al IFD siguiente —`exiftool
 * -validate` los marca como solapados (issue #70)— y no sabe escribir
 * `OffsetTime*`; `piexif-ts` ni siquiera se resuelve con Vite 7. Lo que hace
 * falta aquí es poco y fijo: tres IFD, seis tipos y un segmento APP1.
 *
 * ── Qué produce ────────────────────────────────────────────────────────────
 * `buildExifTiff` → bloque TIFF little-endian («II»): IFD0 con los punteros al
 * IFD Exif y (si hay entradas) al de GPS; sin IFD1, o sea sin miniatura. Las
 * entradas de cada IFD van ordenadas por tag y los valores de más de 4 bytes
 * fuera de línea, en offsets pares, como exige TIFF 6.0.
 *
 * `insertExif` → el JPEG con un APP1 `Exif\0\0` + ese bloque. Todo es puro:
 * bytes de entrada, bytes de salida.
 */

/** Tipos TIFF que se usan. */
export type ExifEntry =
  /** BYTE (1) y UNDEFINED (7): un byte por valor. */
  | { tag: number; type: 1 | 7; value: readonly number[] }
  /** ASCII (2): se añade el NUL final. Solo caracteres ASCII imprimibles. */
  | { tag: number; type: 2; value: string }
  /** SHORT (3) y LONG (4). */
  | { tag: number; type: 3 | 4; value: readonly number[] }
  /** RATIONAL (5): pares [numerador, denominador] sin signo. */
  | { tag: number; type: 5; value: ReadonlyArray<readonly [number, number]> };

export interface ExifIfds {
  ifd0: ExifEntry[];
  exif: ExifEntry[];
  /** Vacío = la foto no lleva GPS: no se escribe ni el IFD ni su puntero. */
  gps: ExifEntry[];
}

const LITTLE_ENDIAN = true;
const TIFF_HEADER_LENGTH = 8;
const EXIF_IFD_POINTER = 0x8769;
const GPS_IFD_POINTER = 0x8825;
const TYPE_SIZE: Record<ExifEntry['type'], number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1 };

/** `Exif\0\0`: cabecera del APP1 de EXIF. */
const EXIF_HEADER = Uint8Array.of(0x45, 0x78, 0x69, 0x66, 0x00, 0x00);
const MAX_SEGMENT_LENGTH = 0xffff;

const valueCount = (e: ExifEntry): number =>
  e.type === 2 ? e.value.length + 1 : e.value.length;

const byteLength = (e: ExifEntry): number => valueCount(e) * TYPE_SIZE[e.type];

/** Lo que ocupa un IFD entero: cabecera, entradas, puntero y datos fuera de línea. */
const ifdLength = (entries: readonly ExifEntry[]): number =>
  entries.reduce((total, e) => {
    const n = byteLength(e);
    return n > 4 ? total + n + (n % 2) : total;
  }, 2 + entries.length * 12 + 4);

/**
 * Un byte por carácter. Lo que no sea ASCII imprimible sale como `?`: EXIF
 * declara ASCII de 7 bits, y así el número de bytes no cambia nunca respecto
 * a `value.length`, que es con lo que se calcula el tamaño.
 */
const asciiBytes = (s: string): number[] =>
  Array.from(s, (c) => {
    const code = c.charCodeAt(0);
    return code >= 0x20 && code <= 0x7e ? code : 0x3f;
  });

function writeValue(view: DataView, at: number, e: ExifEntry): void {
  switch (e.type) {
    case 1:
    case 7:
      e.value.forEach((v, i) => view.setUint8(at + i, v));
      return;
    case 2:
      asciiBytes(e.value).forEach((b, i) => view.setUint8(at + i, b));
      view.setUint8(at + e.value.length, 0);
      return;
    case 3:
      e.value.forEach((v, i) => view.setUint16(at + i * 2, v, LITTLE_ENDIAN));
      return;
    case 4:
      e.value.forEach((v, i) => view.setUint32(at + i * 4, v, LITTLE_ENDIAN));
      return;
    case 5:
      e.value.forEach(([num, den], i) => {
        view.setUint32(at + i * 8, num, LITTLE_ENDIAN);
        view.setUint32(at + i * 8 + 4, den, LITTLE_ENDIAN);
      });
  }
}

/**
 * Escribe un IFD en `start` (offset relativo al inicio del bloque TIFF, que es
 * como cuenta TIFF todos sus offsets). Sin IFD siguiente.
 */
function writeIfd(view: DataView, start: number, entries: readonly ExifEntry[]): void {
  const sorted = [...entries].sort((a, b) => a.tag - b.tag);
  view.setUint16(start, sorted.length, LITTLE_ENDIAN);
  let data = start + 2 + sorted.length * 12 + 4;
  sorted.forEach((e, i) => {
    const p = start + 2 + i * 12;
    const length = byteLength(e);
    view.setUint16(p, e.tag, LITTLE_ENDIAN);
    view.setUint16(p + 2, e.type, LITTLE_ENDIAN);
    view.setUint32(p + 4, valueCount(e), LITTLE_ENDIAN);
    if (length > 4) {
      view.setUint32(p + 8, data, LITTLE_ENDIAN);
      writeValue(view, data, e);
      data += length + (length % 2);
    } else {
      // Valor en línea, justificado a la izquierda del campo de 4 bytes.
      writeValue(view, p + 8, e);
    }
  });
  view.setUint32(start + 2 + sorted.length * 12, 0, LITTLE_ENDIAN);
}

/** Bloque TIFF con IFD0 → Exif (→ GPS). */
export function buildExifTiff({ ifd0, exif, gps }: ExifIfds): Uint8Array<ArrayBuffer> {
  const hasGps = gps.length > 0;
  const withPointers = (exifAt: number, gpsAt: number): ExifEntry[] => [
    ...ifd0.filter((e) => e.tag !== EXIF_IFD_POINTER && e.tag !== GPS_IFD_POINTER),
    { tag: EXIF_IFD_POINTER, type: 4, value: [exifAt] },
    ...(hasGps ? [{ tag: GPS_IFD_POINTER, type: 4, value: [gpsAt] } as ExifEntry] : []),
  ];

  // Los punteros son LONG en línea: su tamaño no depende del valor, así que se
  // puede medir con ceros y rellenar después.
  const exifStart = TIFF_HEADER_LENGTH + ifdLength(withPointers(0, 0));
  const gpsStart = exifStart + ifdLength(exif);
  const total = gpsStart + (hasGps ? ifdLength(gps) : 0);

  const bytes = new Uint8Array(total);
  const view = new DataView(bytes.buffer);
  bytes[0] = 0x49; // «II»: little-endian
  bytes[1] = 0x49;
  view.setUint16(2, 42, LITTLE_ENDIAN);
  view.setUint32(4, TIFF_HEADER_LENGTH, LITTLE_ENDIAN);

  writeIfd(view, TIFF_HEADER_LENGTH, withPointers(exifStart, gpsStart));
  writeIfd(view, exifStart, exif);
  if (hasGps) writeIfd(view, gpsStart, gps);
  return bytes;
}

const isExifApp1 = (segment: Uint8Array): boolean =>
  segment[1] === 0xe1 &&
  segment.length >= 4 + EXIF_HEADER.length &&
  EXIF_HEADER.every((b, i) => segment[4 + i] === b);

function concat(parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/**
 * Mete el bloque TIFF en el JPEG como APP1 `Exif\0\0`.
 *
 * - **Detrás de los APP0 (JFIF)**, que es el orden que escribe exiftool. EXIF
 *   pide «justo tras el SOI» y JFIF pide su APP0 primero: los dos no pueden
 *   cumplirse a la vez, y los lectores reales (exiftool, exifr, Windows,
 *   Android, macOS) recorren los marcadores y aceptan los dos órdenes.
 * - **Sustituye** cualquier APP1 Exif previo: dos bloques EXIF en un fichero
 *   es ambiguo y cada lector se queda con uno distinto.
 * - **No toca un byte** desde el SOS: los datos de imagen salen idénticos.
 *
 * Lanza si la entrada no es un JPEG o su cabecera está truncada: mejor que el
 * llamante conserve la foto original que fabricar un fichero roto.
 */
export function insertExif(jpeg: Uint8Array, tiff: Uint8Array): Uint8Array<ArrayBuffer> {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) {
    throw new Error('insertExif: no es un JPEG (falta el SOI)');
  }
  // La longitud de un segmento se cuenta a sí misma (2 bytes).
  const segmentLength = 2 + EXIF_HEADER.length + tiff.length;
  if (segmentLength > MAX_SEGMENT_LENGTH) {
    throw new Error('insertExif: el bloque EXIF no cabe en un APP1 (64 KB)');
  }
  const app1 = new Uint8Array(2 + segmentLength);
  app1.set([0xff, 0xe1, segmentLength >> 8, segmentLength & 0xff]);
  app1.set(EXIF_HEADER, 4);
  app1.set(tiff, 4 + EXIF_HEADER.length);

  const parts: Uint8Array[] = [jpeg.subarray(0, 2)];
  let pos = 2;
  let inserted = false;
  while (pos < jpeg.length) {
    if (jpeg[pos] !== 0xff || pos + 1 >= jpeg.length) {
      throw new Error('insertExif: cabecera JPEG truncada o corrupta');
    }
    const marker = jpeg[pos + 1];
    // Bytes de relleno (0xFF repetidos) delante de un marcador: son opcionales
    // y se descartan.
    if (marker === 0xff) {
      pos += 1;
      continue;
    }
    if (marker === 0xda) break; // SOS: a partir de aquí, datos de imagen
    if (pos + 4 > jpeg.length) {
      throw new Error('insertExif: cabecera JPEG truncada');
    }
    const length = (jpeg[pos + 2] << 8) | jpeg[pos + 3];
    if (length < 2 || pos + 2 + length > jpeg.length) {
      throw new Error('insertExif: segmento JPEG truncado');
    }
    const segment = jpeg.subarray(pos, pos + 2 + length);
    if (!inserted && marker !== 0xe0) {
      parts.push(app1);
      inserted = true;
    }
    if (!isExifApp1(segment)) parts.push(segment);
    pos += 2 + length;
  }
  if (pos >= jpeg.length) {
    throw new Error('insertExif: cabecera JPEG truncada (no hay SOS)');
  }
  if (!inserted) parts.push(app1);
  parts.push(jpeg.subarray(pos));
  return concat(parts);
}
